import { supabase } from "@/api/supabaseClient";
import type { AnimalCaseRow } from "@/api/cases/CasesApi";

export type CaseRelationType =
  | "mother_of"
  | "sibling_group"
  | "same_identity_suspected"
  | "same_identity_confirmed";

export type CaseRelationView = {
  id: string;
  organization_id: string;
  relation_type: CaseRelationType;
  from_case_id: string;
  to_case_id: string;
  group_key: string | null;
  note: string | null;
  created_at: string;
  created_by: string | null;
  related_case_id: string;
  related_case_number: string | null;
  related_case_name: string | null;
  direction: string;
};

export class CaseRelationsApi {
  static async listForCase(caseId: string): Promise<CaseRelationView[]> {
    const { data, error } = await supabase.rpc("list_case_relations_for_case", {
      p_case_id: caseId,
    });
    if (error) throw error;
    return (data ?? []) as CaseRelationView[];
  }

  static async listSameIdentity(caseId: string): Promise<AnimalCaseRow[]> {
    const { data, error } = await supabase.rpc("list_cases_same_identity", {
      p_case_id: caseId,
    });
    if (error) throw error;
    return (data ?? []) as AnimalCaseRow[];
  }

  static async createMotherOf(input: {
    motherCaseId: string;
    childCaseId: string;
    note?: string | null;
  }) {
    const { data, error } = await supabase.rpc("create_mother_of_relation", {
      p_mother_case_id: input.motherCaseId,
      p_child_case_id: input.childCaseId,
      p_note: input.note ?? null,
    });
    if (error) throw error;
    return data;
  }

  static async linkAsSiblings(caseIds: string[], note?: string | null) {
    const { data, error } = await supabase.rpc("link_cases_as_siblings", {
      p_case_ids: caseIds,
      p_note: note ?? null,
    });
    if (error) throw error;
    return data as string;
  }

  static async createIdentitySuspicion(input: {
    caseAId: string;
    caseBId: string;
    note?: string | null;
  }) {
    const { data, error } = await supabase.rpc(
      "create_same_identity_suspicion",
      {
        p_case_a_id: input.caseAId,
        p_case_b_id: input.caseBId,
        p_note: input.note ?? null,
      }
    );
    if (error) throw error;
    return data;
  }

  static async confirmSameIdentity(input: {
    caseAId: string;
    caseBId: string;
    canonicalCaseId: string;
    reason: string;
  }) {
    const { data, error } = await supabase.rpc("confirm_same_identity", {
      p_case_a_id: input.caseAId,
      p_case_b_id: input.caseBId,
      p_canonical_case_id: input.canonicalCaseId,
      p_reason: input.reason,
    });
    if (error) throw error;
    return data;
  }

  static async undoSameIdentityMerge(relationId: string, reason: string) {
    const { data, error } = await supabase.rpc("undo_same_identity_merge", {
      p_relation_id: relationId,
      p_reason: reason,
    });
    if (error) throw error;
    return data;
  }

  static async canDocumentSuspicion(organizationId: string): Promise<boolean> {
    const { data, error } = await supabase.rpc(
      "can_document_identity_suspicion",
      { p_organization_id: organizationId }
    );
    if (error) throw error;
    return !!data;
  }

  static async isOrgAdmin(organizationId: string): Promise<boolean> {
    const { data, error } = await supabase.rpc("is_org_admin", {
      p_organization_id: organizationId,
    });
    if (error) throw error;
    return !!data;
  }
}
